import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { getBike, getModel, rankByModel, targetModelIds, MY_BIKE_EVENT } from '@theme/my-bike';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Product page "Complete your setup" (frequently bought together).
 * Markup: blocks/voge-complete-setup.liquid, rows: snippets/voge-setup-item.liquid.
 *
 * Candidates, in order of preference:
 *   1. complementary products set in Search & Discovery (server-rendered rows, then intent=complementary),
 *   2. Shopify "related" recommendations (intent=related),
 *   3. the product's model collection (best sellers),
 *   4. universal accessories (data-universal-url) as filler.
 * Rule (rankByModel in assets/my-bike.js): only parts for the current product's model (or the rider's
 * bike when it fits), then universal parts – never other models' parts; apparel/gifts only when the
 * current product is apparel; the product itself and products already in the cart are skipped.
 * Nothing relevant → the block stays hidden. Nothing is pre-checked.
 * The button shows the running total ("Add 2 items · €57,90"); "Add" posts all checked rows (their
 * selected variant, quantity 1) to /cart/add.js in one request and dispatches CartLinesUpdateEvent so
 * the cart drawer, bubble and free-shipping bar refresh. No discount is involved.
 */

/**
 * Formats cents like Liquid's `money` filter using a sample rendered by Liquid (123456 | money).
 * @param {number} cents
 * @param {string} sample
 * @returns {string}
 */
function formatMoney(cents, sample) {
  const match = /^(.*?)1(\D?)234(\D)56(.*)$/s.exec(sample || '');
  if (!match) return (cents / 100).toFixed(2);
  const [, prefix, group, decimal, suffix] = match;
  const units = Math.floor(Math.abs(cents) / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, group || '');
  const minor = String(Math.abs(cents) % 100).padStart(2, '0');
  return `${cents < 0 ? '-' : ''}${prefix}${units}${decimal}${minor}${suffix}`;
}

/** @returns {Record<string, string>} */
function routes() {
  // @ts-ignore – `Theme` is a global declared in snippets/scripts.liquid
  return typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
}

class CompleteSetup extends Component {
  #listeners = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#listeners;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(MY_BIKE_EVENT, () => this.#updateHeading(), { signal });
    this.#updateHeading();
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
  }

  async #load() {
    const productId = this.dataset.productId;
    if (!productId) return;
    const limit = Number(this.dataset.limit || '3');
    const targets = targetModelIds((this.dataset.collectionIds || '').split(',').filter(Boolean));
    const allowGifts = this.dataset.gift === 'true';
    const exclude = new Set([productId, ...(this.dataset.cartIds || '').split(',').filter(Boolean)]);
    const sectionId = this.dataset.sectionId || 'voge-complete-setup';
    const base = routes().product_recommendations_url || '/recommendations/products';

    /** @param {HTMLElement[]} list */
    const relevant = (list) => rankByModel(list, targets, allowGifts);

    // 1. Server-rendered complementary products (Search & Discovery)
    /** @type {HTMLElement[]} */
    let rows = this.#merge([], Array.from(this.querySelectorAll('[data-setup-list] [data-setup-item]')), exclude);

    // 2. Shopify recommendations
    for (const intent of ['complementary', 'related']) {
      if (relevant(rows).length >= limit) break;
      const url = new URL(base, location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', intent);
      url.searchParams.set('section_id', sectionId);
      rows = this.#merge(rows, await this.#fetchRows(url), exclude);
    }

    // 3. Model collection, 4. universal accessories
    const model = targets.length ? getModel(targets[0]) : undefined;
    for (const fill of [model?.url, this.dataset.universalUrl]) {
      if (!fill || relevant(rows).length >= limit) continue;
      const url = new URL(fill, location.origin);
      url.searchParams.set('section_id', sectionId);
      rows = this.#merge(rows, await this.#fetchRows(url), exclude);
    }

    rows = relevant(rows).slice(0, limit);
    const target = this.querySelector('[data-setup-list]');
    if (!rows.length || !target) {
      this.hidden = true;
      return;
    }

    // Keep IDs unique per block instance.
    const uid = this.dataset.uid || 'setup';
    rows.forEach((row, index) => {
      const input = row.querySelector('[data-setup-check]');
      if (!input) return;
      const id = `${uid}-row-${index}-${row.dataset.productId}`;
      input.id = id;
      if (input instanceof HTMLInputElement) input.checked = false;
      const title = row.querySelector('[data-setup-title]');
      if (title) {
        title.id = `${id}-title`;
        input.setAttribute('aria-labelledby', title.id);
      }
    });

    const list = document.createElement('ul');
    list.className = 'voge-setup__items';
    list.setAttribute('role', 'list');
    list.append(...rows);
    target.replaceChildren(list);
    this.dataset.loaded = 'true';
    this.hidden = false;
    this.#sync();
  }

  /**
   * @param {URL} url
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchRows(url) {
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) return [];
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return /** @type {HTMLElement[]} */ (Array.from(doc.querySelectorAll('.voge-setup__items [data-setup-item]')));
    } catch {
      return [];
    }
  }

  /**
   * Appends rows whose product is not yet listed and not excluded (current product, cart items).
   * @param {HTMLElement[]} rows
   * @param {HTMLElement[]} more
   * @param {Set<string>} exclude
   */
  #merge(rows, more, exclude) {
    const seen = new Set([...exclude, ...rows.map((row) => row.dataset.productId || '')]);
    const added = more.filter((row) => {
      const id = row.dataset.productId || '';
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return [...rows, ...added];
  }

  #updateHeading() {
    const heading = this.querySelector('[data-setup-heading]');
    if (!heading) return;
    const bike = getBike();
    const ids = (this.dataset.collectionIds || '').split(',');
    heading.textContent =
      bike && ids.includes(String(bike.id))
        ? (this.dataset.headingModel || '').replace('[model]', bike.title)
        : this.dataset.heading || heading.textContent || '';
  }

  /** @returns {HTMLElement[]} */
  get #checkedRows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-setup-item]'))).filter(
      (row) => /** @type {HTMLInputElement | null} */ (row.querySelector('[data-setup-check]'))?.checked
    );
  }

  #sync() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-setup-add]'));
    const label = this.querySelector('[data-setup-label]');
    const rows = this.#checkedRows;
    const total = rows.reduce((sum, row) => {
      const price = /** @type {HTMLElement | null} */ (row.querySelector('[data-setup-price]'));
      return sum + Number(price?.dataset.cents || 0);
    }, 0);
    if (label) {
      // Templates from voge.product.setup_add_count (one/other), rendered with [price] and 999999 placeholders
      const template = rows.length === 1 ? this.dataset.labelOne || '' : this.dataset.labelOther || '';
      label.textContent = rows.length
        ? template.replace('999999', String(rows.length)).replace('[price]', formatMoney(total, this.dataset.moneySample || ''))
        : this.dataset.labelEmpty || label.textContent || '';
    }
    if (button && button.getAttribute('aria-busy') !== 'true') button.disabled = rows.length === 0;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-setup-variant]')) {
      const option = target.selectedOptions[0];
      const row = target.closest('[data-setup-item]');
      const price = /** @type {HTMLElement | null | undefined} */ (row?.querySelector('[data-setup-price]'));
      if (price && option?.dataset.price) {
        price.textContent = option.dataset.price;
        price.dataset.cents = option.dataset.cents || price.dataset.cents;
      }
      const chip = row?.querySelector('[data-setup-chip-label]');
      if (chip && option) chip.textContent = option.textContent;
      // Choosing an option implies the shopper wants the item.
      const check = /** @type {HTMLInputElement | null} */ (
        target.closest('[data-setup-item]')?.querySelector('[data-setup-check]')
      );
      if (check) check.checked = true;
    }
    this.#setStatus('');
    this.#sync();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-setup-add]');
    if (button instanceof HTMLButtonElement) this.#addSelected(button);
  };

  /**
   * @param {string} message
   * @param {'error' | ''} [tone]
   */
  #setStatus(message, tone = '') {
    const status = /** @type {HTMLElement | null} */ (this.querySelector('[data-setup-status]'));
    if (!status) return;
    status.textContent = message;
    status.dataset.tone = tone;
  }

  /** @param {HTMLButtonElement} button */
  async #addSelected(button) {
    const rows = this.#checkedRows;
    if (!rows.length || button.getAttribute('aria-busy') === 'true') return;

    const items = rows
      .map((row) => {
        const field = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
          row.querySelector('[data-setup-variant]')
        );
        return { id: Number(field?.value), quantity: 1 };
      })
      .filter((item) => item.id > 0);
    if (!items.length) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    this.#setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      /** @type {Record<string, unknown>} */
      const body = { items };
      if (sectionIds.length) body.sections = sectionIds.join(',');
      const addUrl = (routes().cart_add_url || '/cart/add').replace(/(\.js)?$/, '.js');
      const response = await fetch(addUrl, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
      }).then((res) => res.json());

      const cart = await fetch(`${routes().cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      const failed = Boolean(response.status);
      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#setStatus(response.description || this.dataset.msgError || '', 'error');
      } else {
        this.#setStatus(this.dataset.msgAdded || '');
        for (const row of rows) {
          const check = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-setup-check]'));
          if (check) check.checked = false;
        }
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'complete-setup',
          sourceId: 'complete-setup',
          itemCount: items.length,
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: /** @type {Error} */ (error)?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#setStatus(this.dataset.msgError || '', 'error');
    } finally {
      button.removeAttribute('aria-busy');
      this.#sync();
    }
  }
}

if (!customElements.get('complete-setup')) {
  customElements.define('complete-setup', CompleteSetup);
}